/** Remove markdown horizontal-rule lines (---, ***, ___) — common LLM section separators, not clinical content. */
export function stripMarkdownHorizontalRules(text: string): string {
  return text
    .replace(/^\s*(-{3,}|\*{3,}|_{3,})\s*$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Strip paired **markdown** bold markers for heuristics only. */
export function stripMarkdownBold(s: string): string {
  return s.replace(/\*\*([^*]+)\*\*/g, '$1').trim();
}

/** Remove leading list/number prefixes used in SOAP text. */
export function stripLinePrefix(line: string): string {
  let t = line.trimStart();
  t = t.replace(/^•\s+/, '');
  t = t.replace(/^\d{1,2}\.\s+/, '');
  return t.trimStart();
}

/** Drop leading {@code 1. } / {@code 2. } markers from Objective (and similar) bodies for Visits display. */
export function stripLeadingSectionNumbers(text: string): string {
  return (text ?? '')
    .replace(/\r/g, '')
    .split('\n')
    .map((line) => line.replace(/^\s*\d{1,2}\.\s+/, ''))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function matchLabelColon(line: string): { label: string; content: string } | null {
  const plain = stripMarkdownBold(stripLinePrefix(line));
  const m = plain.match(/^([A-Za-z][A-Za-z0-9\s\/\-()]{0,44}):\s*(.*)$/);
  if (!m) {
    return null;
  }
  const label = (m[1] || '').trim();
  const content = (m[2] || '').trim();
  if (!label || label.length > 45) {
    return null;
  }
  if (/[.?!]$/.test(label)) {
    return null;
  }
  if (/^Problem\s*\d+$/i.test(label)) {
    return null;
  }
  return { label, content };
}

export function isNumberedSectionHeading(line: string): boolean {
  return /^\s*\d{1,2}\.\s+\S/.test(line);
}

/** Heading-only line: "Label:" with no inline content. */
export function isHeadingOnlyLine(line: string): boolean {
  const sub = matchLabelColon(line);
  return !!sub && !sub.content;
}

/** True for lines that start a new SOAP block (not inline sub-labels like "Occupation: ..."). */
export function isMajorSectionBreak(line: string): boolean {
  if (!line.trim()) {
    return false;
  }
  if (isNumberedSectionHeading(line)) {
    return true;
  }
  return isHeadingOnlyLine(line);
}

export function isLikelyShortHeading(line: string): boolean {
  const t = stripMarkdownBold(stripLinePrefix(line));
  if (!t || t.length > 70) {
    return false;
  }
  if (/,\s*$/.test(t)) {
    return false;
  }
  if (/:\s*$/.test(t) && t.split(/\s+/).filter(Boolean).length > 5) {
    return false;
  }
  if (/[.?!]$/.test(t) && !/:\s*$/.test(t)) {
    return false;
  }
  const wordCount = t.split(/\s+/).filter(Boolean).length;
  return wordCount >= 1 && wordCount <= 12;
}

/**
 * Flatten markdown list indentation and turn plain lines under heading-only
 * sections into bullet lines so soapToHtml can render a proper nested list.
 */
export function normalizeListSections(text: string): string {
  const lines = text.replace(/\r/g, '').split('\n');
  const out: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = (lines[i] ?? '').trimStart();
    if (!line.trim()) {
      if (out.length > 0 && out[out.length - 1] !== '') {
        out.push('');
      }
      continue;
    }

    out.push(line);

    if (!isHeadingOnlyLine(line)) {
      continue;
    }

    let j = i + 1;
    while (j < lines.length) {
      const next = (lines[j] ?? '').trimStart();
      if (!next.trim()) {
        j++;
        continue;
      }
      if (isHeadingOnlyLine(next) || isNumberedSectionHeading(next)) {
        break;
      }

      if (/^•\s/.test(next) || isNumberedSectionHeading(next)) {
        out.push(next.trim());
      } else {
        out.push('• ' + next.trim());
      }
      j++;
    }
    i = j - 1;
  }

  return out.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

/** Field and ROS labels that must not appear as HPI sub-headings. Longest first. */
const SUBJECTIVE_STORY_LABELS: string[] = [
  'ideas, concerns, expectations, goals for care, impact on daily life',
  'history of present illness',
  'identifying information',
  'medications and supplements',
  'cognitive/behavioral symptoms',
  'living situation/caregiver',
  'prior treatments/response',
  'patient presentation',
  'past medical history',
  'past surgical history',
  'associated symptoms',
  'pertinent negatives',
  'review of systems',
  'patient perspective',
  'functional status',
  'quality/character',
  'chief complaints',
  'family history',
  'social history',
  'chief complaint',
  'medical history',
  'pain inventory',
  'sleep history',
  'diet/nutrition',
  'bowel/bladder',
  'timing/pattern',
  'context/trigger',
  'modifying factors',
  'prior evaluations',
  'living situation',
  'prior treatments',
  'allergy/immunology',
  'gastrointestinal',
  'cardiovascular',
  'musculoskeletal',
  'genitourinary',
  'constitutional',
  'neurological',
  'psychological',
  'psychiatric',
  'integumentary',
  'prior testing',
  'adls iadls',
  'medications',
  'respiratory',
  'progression',
  'occupation',
  'caregiver',
  'hydration',
  'nutrition',
  'allergies',
  'cognitive',
  'endocrine',
  'heme lymph',
  'character',
  'severity',
  'duration',
  'location',
  'quality',
  'pattern',
  'context',
  'trigger',
  'timing',
  'general',
  'bladder',
  'bowel',
  'heent',
  'onset',
  'adls',
  'iadls',
  'diet',
  'skin',
  'neuro',
  'hpi',
  'pmh',
  'psh',
  'ros',
  'cc',
];

function normalizeStoryLabelKey(line: string): string {
  let t = stripMarkdownBold(stripLinePrefix(line)).toLowerCase();
  t = t.replace(/\([^)]*\)/g, ' ');
  t = t.replace(/&/g, ' ');
  t = t.replace(/\//g, ' ');
  const colon = t.indexOf(':');
  if (colon >= 0) {
    t = t.slice(0, colon);
  }
  return t.replace(/\s+/g, ' ').trim();
}

/** Canonical inner HPI headings that must remain visible. */
export function canonicalHpiKeepHeading(line: string): string | null {
  const key = normalizeStoryLabelKey(line);
  if (key === 'chief complaint' || key === 'chief complaints' || key === 'cc') {
    return 'Chief Complaint';
  }
  if (key === 'history of present illness' || key === 'hpi') {
    return 'History of Present Illness';
  }
  if (
    key === 'medications and supplements' ||
    key === 'medications supplements' ||
    key === 'medications' ||
    key === 'medication'
  ) {
    return 'Medications & supplements';
  }
  if (key === 'review of systems' || key === 'ros') {
    return 'Review of Systems';
  }
  return null;
}

function matchingStoryLabel(key: string): string | null {
  if (!key) {
    return null;
  }
  for (const label of SUBJECTIVE_STORY_LABELS) {
    const normalized = label.replace(/\//g, ' ').replace(/\s+/g, ' ').trim();
    if (key === normalized || key === label) {
      return label;
    }
  }
  return null;
}

/**
 * Drop HPI/ROS field labels (Onset, Progression, Living situation, General, …)
 * so Subjective can render as a running narrative, while keeping Chief Complaint,
 * History of Present Illness, Medications & supplements, and Review of Systems.
 */
export function flattenSubjectiveStoryLabels(text: string): string {
  return (text ?? '')
    .replace(/\r/g, '')
    .split('\n')
    .map((raw) => {
      const line = stripLinePrefix(raw).trim();
      if (!line) {
        return '';
      }
      const keep = canonicalHpiKeepHeading(line);
      if (keep) {
        const colon = matchLabelColon(line);
        if (colon?.content) {
          return `${keep}\n${colon.content}`;
        }
        return keep;
      }
      const colon = matchLabelColon(line);
      if (colon) {
        const labelKey = normalizeStoryLabelKey(colon.label);
        if (matchingStoryLabel(labelKey)) {
          return colon.content;
        }
        return line;
      }
      const key = normalizeStoryLabelKey(line);
      if (matchingStoryLabel(key)) {
        return '';
      }
      return line;
    })
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Join wrapped lines into paragraphs (blank line = paragraph break). */
export function joinLinesToParagraphs(text: string): string {
  const paras: string[] = [];
  let current: string[] = [];
  const flush = (): void => {
    if (!current.length) {
      return;
    }
    paras.push(current.join(' ').replace(/\s+/g, ' ').trim());
    current = [];
  };
  for (const raw of (text ?? '').replace(/\r/g, '').split('\n')) {
    const line = raw.trim();
    if (!line) {
      flush();
      continue;
    }
    if (/^[-•*]\s+/.test(line) || canonicalHpiKeepHeading(line)) {
      flush();
      paras.push(line);
      continue;
    }
    current.push(line);
  }
  flush();
  return paras.join('\n\n').trim();
}

/** Merge many short paragraphs into at most {@code max} longer ones. */
export function coalesceNarrativeParagraphs(text: string, max = 4): string {
  const paras = joinLinesToParagraphs(text)
    .split(/\n\s*\n/)
    .map((p) => p.replace(/\s+/g, ' ').trim())
    .filter((p) => p && !canonicalHpiKeepHeading(p) && !/^[-•*]\s+/.test(p));
  if (!paras.length) {
    return '';
  }
  if (paras.length <= max) {
    return paras.join('\n\n');
  }
  const groups: string[] = Array.from({ length: max }, () => '');
  for (let i = 0; i < paras.length; i++) {
    let g = Math.floor((i * max) / paras.length);
    if (g >= max) {
      g = max - 1;
    }
    groups[g] = groups[g] ? `${groups[g]} ${paras[i]}` : paras[i];
  }
  return groups.filter(Boolean).join('\n\n');
}

/**
 * One "- " bullet per medication/supplement, keeping dose, route, and frequency when present.
 */
export function formatMedicationsAsBullets(text: string): string {
  const items: string[] = [];
  for (const raw of (text ?? '').replace(/\r/g, '').split('\n')) {
    const line = raw.trim();
    if (!line || canonicalHpiKeepHeading(line)) {
      continue;
    }
    const stripped = line.replace(/^[-•*]\s+/, '').trim();
    if (!stripped) {
      continue;
    }
    for (const piece of splitMedicationItems(stripped)) {
      if (piece) {
        items.push(piece);
      }
    }
  }
  return items
    .map((item) => {
      let t = item.replace(/^[-•*]\s+/, '').trim();
      if (t && !/[.!?]$/.test(t)) {
        t += '.';
      }
      return `- ${t}`;
    })
    .join('\n');
}

function splitMedicationItems(text: string): string[] {
  const t = text.trim();
  if (!t) {
    return [];
  }
  const semi = t.split(/\s*;\s*/).map((s) => s.trim()).filter(Boolean);
  const chunks = semi.length > 1 ? semi : [t];
  const withAnd: string[] = [];
  for (const chunk of chunks) {
    const parts = chunk.split(/\s+and\s+(?=[A-Z])/);
    if (parts.length > 1) {
      withAnd.push(...parts.map((p) => p.trim()).filter(Boolean));
    } else {
      withAnd.push(chunk);
    }
  }
  const sentences: string[] = [];
  for (const chunk of withAnd) {
    const sents = chunk.split(/(?<=\.)\s+(?=[A-Z])/);
    if (sents.length > 1 && chunk.length > 80) {
      sentences.push(...sents.map((s) => s.trim()).filter(Boolean));
    } else {
      sentences.push(chunk);
    }
  }
  return sentences;
}
